// ─────────────────────────────────────────────────────────────────────────────
// DEPTH FUSION — Phase 13-15
// Monocular depth from MediaPipe landmark Z coords + segmentation mask
// Produces a depth map for GPU stencil layering
// In production: replace with ARKit LiDAR depth or ARCore Depth API
// ─────────────────────────────────────────────────────────────────────────────
import type { NormalizedLandmark } from "./pose";
import { POSE_IDX } from "./pose";

export interface DepthLayer {
  /** 0 = furthest, 1 = closest */
  foreground: Float32Array;
  width: number;
  height: number;
}

// ── Estimate relative depth from pose Z values ────────────────────────────────
// MediaPipe Z is relative to hip, negative = closer to camera
// Returns a per-body-region depth estimate (not per-pixel)
export interface BodyDepthEstimate {
  headZ: number;
  torsoZ: number;
  leftArmZ: number;
  rightArmZ: number;
  leftLegZ: number;
  rightLegZ: number;
}

export function estimateBodyDepth(landmarks: NormalizedLandmark[]): BodyDepthEstimate {
  const z = (idx: number) => -(landmarks[idx]?.z ?? 0); // flip: negative = closer

  return {
    headZ:      z(0),
    torsoZ:     (z(POSE_IDX.LEFT_SHOULDER) + z(POSE_IDX.RIGHT_SHOULDER)) / 2,
    leftArmZ:   (z(POSE_IDX.LEFT_ELBOW) + z(POSE_IDX.LEFT_WRIST)) / 2,
    rightArmZ:  (z(POSE_IDX.RIGHT_ELBOW) + z(POSE_IDX.RIGHT_WRIST)) / 2,
    leftLegZ:   (z(POSE_IDX.LEFT_KNEE)  + z(27)) / 2,
    rightLegZ:  (z(POSE_IDX.RIGHT_KNEE) + z(28)) / 2,
  };
}

// ── GPU Stencil config — Three.js stencil layer assignment ────────────────────
// Phase 4 milestone: arms render in front of jacket, torso hides under shirt
export const STENCIL_LAYER = {
  BACKGROUND:     0,  // video plane
  GARMENT_BACK:   1,  // back of garment (behind body)
  TORSO:          2,  // torso region
  GARMENT_FRONT:  3,  // front of garment (in front of torso)
  ARMS:           4,  // arms (in front of garment)
  HEAD:           5,  // head (always front)
} as const;

// ── Depth-sorted draw order for garment layers ────────────────────────────────
export function computeRenderOrder(
  depth: BodyDepthEstimate,
  garmentZ: number // Z of the garment plane
): { garmentBehindArms: boolean; garmentBehindTorso: boolean } {
  const avgArmZ = (depth.leftArmZ + depth.rightArmZ) / 2;
  return {
    garmentBehindArms:  garmentZ < avgArmZ,
    garmentBehindTorso: garmentZ < depth.torsoZ,
  };
}

// ── Refine segmentation mask with depth data ──────────────────────────────────
// Boosts confidence for regions that should be foreground based on landmark depth
export function refineMaskWithDepth(
  mask: Float32Array,
  landmarks: NormalizedLandmark[],
  width: number,
  height: number,
  isMirrored: boolean
): Float32Array {
  const refined = new Float32Array(mask);
  const depth = estimateBodyDepth(landmarks);

  // For each landmark group, boost the mask in that region
  const boostRegion = (
    cx: number, cy: number,
    radius: number,
    boost: number
  ) => {
    const px = Math.round((isMirrored ? 1 - cx : cx) * width);
    const py = Math.round(cy * height);
    const r2 = (radius * width) ** 2;

    for (let y = Math.max(0, py - radius * height); y < Math.min(height, py + radius * height); y++) {
      for (let x = Math.max(0, px - radius * width); x < Math.min(width, px + radius * width); x++) {
        const dx = x - px, dy = y - py;
        if (dx * dx + dy * dy > r2) continue;
        const idx = Math.round(y) * width + Math.round(x);
        refined[idx] = Math.min(1, refined[idx] + boost);
      }
    }
  };

  // Arms get highest boost (they must occlude garment)
  const armBoost = Math.max(0, depth.leftArmZ - depth.torsoZ) * 0.3;
  const lElbow = landmarks[POSE_IDX.LEFT_ELBOW];
  const rElbow = landmarks[POSE_IDX.RIGHT_ELBOW];
  const lWrist = landmarks[POSE_IDX.LEFT_WRIST];
  const rWrist = landmarks[POSE_IDX.RIGHT_WRIST];

  if (lElbow && (lElbow.visibility ?? 1) > 0.5) boostRegion(lElbow.x, lElbow.y, 0.08, armBoost);
  if (rElbow && (rElbow.visibility ?? 1) > 0.5) boostRegion(rElbow.x, rElbow.y, 0.08, armBoost);
  if (lWrist && (lWrist.visibility ?? 1) > 0.5) boostRegion(lWrist.x, lWrist.y, 0.06, armBoost);
  if (rWrist && (rWrist.visibility ?? 1) > 0.5) boostRegion(rWrist.x, rWrist.y, 0.06, armBoost);

  return refined;
}

// ── ML monocular depth placeholder ───────────────────────────────────────────
// In production: replace with MiDaS via ONNX.js or TF.js
// For now: returns depth estimate from landmark Z coords interpolated to pixel grid
export function estimateMonocularDepth(
  landmarks: NormalizedLandmark[],
  width: number,
  height: number,
  isMirrored: boolean
): Float32Array {
  const depth = new Float32Array(width * height).fill(0.5); // neutral depth

  // Splat landmark depth values onto a rough body shape
  const splat = (lm: NormalizedLandmark, radius: number, depthVal: number) => {
    const px = Math.round((isMirrored ? 1 - lm.x : lm.x) * width);
    const py = Math.round(lm.y * height);
    const r = Math.round(radius * width);

    for (let y = Math.max(0, py - r); y < Math.min(height, py + r); y++) {
      for (let x = Math.max(0, px - r); x < Math.min(width, px + r); x++) {
        const dx = x - px, dy = y - py;
        const dist = Math.sqrt(dx * dx + dy * dy) / r;
        if (dist > 1) continue;
        const weight = 1 - dist;
        const idx = y * width + x;
        depth[idx] = depth[idx] * (1 - weight) + depthVal * weight;
      }
    }
  };

  for (const lm of landmarks) {
    if ((lm.visibility ?? 1) < 0.3) continue;
    const d = Math.max(0, Math.min(1, 0.5 - lm.z)); // closer = higher
    splat(lm, 0.12, d);
  }

  return depth;
}
